<script setup lang="ts">
  import { computed } from 'vue';
  import {
    DefaultTable,
    NoDocuments,
    SearchNoResults,
    TableHeaderSection,
    TableLoading,
    TabsMenu,
  } from '@/shared/ui';
  import NoFilesIcon from '@/shared/assets/images/no-files.svg';
  import type { OrderOptions } from '@/shared/lib';
  import ModelTableRow from './ModelTableRow.vue';
  import DraftTableRow from './DraftTableRow.vue';
  import RejectedTableRow from './RejectedTableRow.vue';
  import type { ModelItem, DraftItem, RejectedItem, RecordItem } from '../model/models.types';

  interface SubTabOption {
    id: string;
    text: string;
    badge?: string;
  }

  const props = withDefaults(
    defineProps<{
      records: RecordItem[];
      subTabOptions: SubTabOption[];
      activeSubTab: string;
      orderOptions?: OrderOptions[];
      currentSort?: string;
      currentOrder?: string;
      isSearchActive?: boolean;
      isFilterActive?: boolean;
      loading?: boolean;
      loadingColumnSize?: number;
      selectedIds?: ReadonlySet<number>;
      canDelete?: boolean;
      canRename?: boolean;
    }>(),
    {
      orderOptions: () => [],
      currentSort: undefined,
      currentOrder: undefined,
      isSearchActive: false,
      isFilterActive: false,
      loading: false,
      loadingColumnSize: 4,
      selectedIds: () => new Set<number>(),
      canDelete: true,
      canRename: true,
    }
  );

  const emit = defineEmits<{
    'tab-change': [id: string];
    'update:sort': [sort: string, order: string];
    'filter-clicked': [];
    'toggle-selection': [id: number];
    'toggle-all': [ids: number[]];
    'bulk-delete': [];
    rename: [model: ModelItem];
    delete: [model: ModelItem];
    'rename-draft': [draft: DraftItem];
    'delete-draft': [draft: DraftItem];
    'delete-rejected': [rejected: RejectedItem];
  }>();

  const TAB_COLUMNS: Record<string, { headers: string[]; expand: string; itemType: string }> = {
    modelos: {
      headers: ['Título', 'Categorias', 'Ações'],
      expand: 'Categorias',
      itemType: 'modelos',
    },
    rascunhos: {
      headers: ['Registro', 'Título', 'Data de salvamento', 'Ações'],
      expand: 'Data de salvamento',
      itemType: 'rascunhos',
    },
    reprovados: {
      headers: ['Registro', 'Título', 'Rev.', 'Data da reprovação', 'Ações'],
      expand: 'Data da reprovação',
      itemType: 'registros',
    },
  };

  const isDrafts = computed(() => props.activeSubTab === 'rascunhos');
  const isRejected = computed(() => props.activeSubTab === 'reprovados');
  const tabColumns = computed(() => TAB_COLUMNS[props.activeSubTab] ?? TAB_COLUMNS.modelos);
  const isEmpty = computed(() => props.records.length === 0);
  const emptyColspan = computed(() => tabColumns.value.headers.length + 1);
  const emptyTitle = computed(() => {
    if (props.isFilterActive) {
      return isDrafts.value ? 'Nenhum rascunho encontrado' : 'Nenhum registro encontrado';
    }
    if (isDrafts.value) return 'Ops! Parece que ainda não há rascunhos';
    if (isRejected.value) return 'Ops! Parece que ainda não há registros reprovados';
    return 'Ops! Parece que ainda não há modelos';
  });

  const selectedCount = computed(() => props.selectedIds.size);
  const hasSelection = computed(() => selectedCount.value > 0);
  const allSelected = computed(
    () => props.records.length > 0 && selectedCount.value === props.records.length
  );

  const isSelected = (id: number) => props.selectedIds.has(id);

  const customThClass = (header: string, index: number) => {
    const baseClass = 'text-xs font-semibold text-gray-07 py-4.5 align-middle';
    const inset = index === 0 ? '' : 'pl-6';
    if (header === 'Ações') {
      return `${baseClass} text-left pl-6 pr-6 whitespace-nowrap`;
    }
    if (header === tabColumns.value.expand) {
      return `${baseClass} text-left ${inset} w-full`;
    }
    if (header === 'Título') {
      const titleMinWidth = props.activeSubTab === 'modelos' ? 'min-w-96' : 'min-w-80';
      return `${baseClass} text-left ${inset} whitespace-nowrap ${titleMinWidth}`;
    }
    return `${baseClass} text-left ${inset} whitespace-nowrap`;
  };

  const handleToggleAll = () => {
    emit(
      'toggle-all',
      props.records.map((record) => record.id)
    );
  };
</script>

<template>
  <DefaultTable class="divide-y divide-gray-02 min-w-full">
    <TableHeaderSection
      :headers="tabColumns.headers"
      :item-count="props.records.length"
      :item-type="tabColumns.itemType"
      :has-selected="hasSelection"
      :selected-count="selectedCount"
      :selected-documents="[...props.selectedIds]"
      :all-selected="allSelected"
      :order-options="props.orderOptions"
      :current-sort="props.currentSort"
      :current-order="props.currentOrder"
      :is-filter-active="props.isFilterActive"
      :show-column-headers="!props.loading && !isEmpty"
      :th-class="customThClass"
      th-checkbox-class="pl-4 pr-2 align-middle leading-[0]"
      :has-delete="props.canDelete"
      :delete-disabled="!hasSelection"
      @toggle-all="handleToggleAll"
      @delete="emit('bulk-delete')"
      @update:sort="(sort: string, order: string) => emit('update:sort', sort, order)"
      @filter-clicked="emit('filter-clicked')"
    >
      <template #tabs>
        <TabsMenu
          :options="props.subTabOptions"
          :selected-option="props.activeSubTab"
          visual-variant="neutral"
          @option-selected="emit('tab-change', $event)"
        />
      </template>
    </TableHeaderSection>
    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <tr v-if="props.loading">
        <td :colspan="emptyColspan">
          <TableLoading
            data-testid="models-loading"
            :column-size="props.loadingColumnSize"
          />
        </td>
      </tr>
      <tr v-else-if="isEmpty && props.isSearchActive">
        <td
          :colspan="emptyColspan"
          class="pb-15"
        >
          <SearchNoResults data-testid="models-no-results" />
        </td>
      </tr>
      <tr v-else-if="isEmpty">
        <td
          :colspan="emptyColspan"
          class="pb-15"
        >
          <NoDocuments
            data-testid="models-empty"
            :icon="NoFilesIcon"
            :title="emptyTitle"
          />
        </td>
      </tr>
      <template v-else-if="isDrafts">
        <DraftTableRow
          v-for="draft in (props.records as DraftItem[])"
          :key="draft.id"
          :draft="draft"
          :is-selected="isSelected(draft.id)"
          :can-rename="props.canRename"
          :can-delete="props.canDelete"
          @toggle-selection="emit('toggle-selection', $event)"
          @rename="emit('rename-draft', $event)"
          @delete="emit('delete-draft', $event)"
        />
      </template>
      <template v-else-if="isRejected">
        <RejectedTableRow
          v-for="rejected in (props.records as RejectedItem[])"
          :key="rejected.id"
          :rejected="rejected"
          :is-selected="isSelected(rejected.id)"
          :can-delete="props.canDelete"
          @toggle-selection="emit('toggle-selection', $event)"
          @delete="emit('delete-rejected', $event)"
        />
      </template>
      <template v-else>
        <ModelTableRow
          v-for="model in (props.records as ModelItem[])"
          :key="model.id"
          :model="model"
          :is-selected="isSelected(model.id)"
          :can-rename="props.canRename"
          :can-delete="props.canDelete"
          @toggle-selection="emit('toggle-selection', $event)"
          @rename="emit('rename', $event)"
          @delete="emit('delete', $event)"
        />
      </template>
    </tbody>
  </DefaultTable>
</template>
